import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TASK_FLOW_PARAM } from '../components/work-queue/tasks/useTaskFlowParam';
import {
    DM_CONTROL_PARAM,
    DM_CONTROL_STEP_PARAM
} from '../components/patient-files/actions/dm/control/useDmControlRoute';
import {
    type WorkQueueCase,
    type OutgoingAction,
    type SortBy,
    type SortDir,
    type StatusMeta
} from '../services/workQueue';
import {
    useMeta,
    useGroups,
    useCases,
    useInfiniteCases,
    useCase,
    type PriorityCounts
} from '../hooks/useWorkQueue';
import { useAssigneeFilter } from '../hooks/useAssigneeFilter';
import { ASSIGNEE_NULL_SENTINEL } from '../utils/assigneeFilter';
import { statusIdToSlug, slugToStatusId } from '../services/workflowStepSlug';
import { GroupNavColumn } from '../components/work-queue/GroupNavColumn';
import { CaseList } from '../components/work-queue/CaseList';
import { DetailPanel } from '../components/work-queue/DetailPanel';
import { ActionModal } from '../components/work-queue/ActionModal';
import { TaskSurface } from '../components/work-queue/TaskSurface';
import { useNextCaseLink } from '../components/work-queue/useNextCaseLink';
import { optionLabels } from '../components/work-queue/queueListChips';
import { useTaskFocusMode } from '../components/work-queue/useTaskFocusMode';
import { WorkQueueToolbar } from '../components/work-queue/WorkQueueToolbar';
import { AppointmentsOverlay } from '../components/work-queue/appointments/AppointmentsOverlay';
import { APPOINTMENTS_OVERLAY_PARAMS } from '../components/work-queue/appointments/appointmentsOverlayParams';
import { buildStatusLookup } from '../components/work-queue/util';
import { metaFor } from '../components/work-queue/versionedStatusMeta';
import { ASSIGNEE_URL_PARAM } from '../components/work-queue/constants';
import { usePageHeader } from '../providers/PageHeaderProvider';
import { usePageTitle } from '../hooks/usePageTitle';
import { encodeUrlArray, useUrlArrayParam } from '../hooks/useUrlArrayParam';
import { pushSearchHistory } from '../components/work-queue/searchHistory';

const DEFAULT_PAGE_SIZE = 10;
// Chunk size for the "scroll" view mode's infinite query. Small enough to
// keep first paint snappy; the IntersectionObserver sentinel in CaseList
// fetches the next chunk when the user nears the bottom.
const SCROLL_PAGE_SIZE = 30;
const VIEW_MODE_KEY = 'odo:wq:view-mode';
const FILTERS_STORAGE_KEY = 'odo:wq:filters';
type ViewMode = 'paged' | 'scroll';

function formatToday(): string {
    return new Date().toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
    });
}

const MONITORING_LABEL_TO_WIRE: Record<string, string> = {
    'Not started': 'not-started',
    Started: 'started',
    Paused: 'paused',
    Stopped: 'stopped'
};

const MONITORING_WIRE_TO_LABEL: Record<string, string> = {
    'not-started': 'Not started',
    started: 'Started',
    paused: 'Paused',
    stopped: 'Stopped'
};

// Priority display labels (capitalised) ↔ wire values (lowercase).
const PRIORITY_LABEL_TO_WIRE: Record<string, string> = {
    Extreme: 'extreme',
    High: 'high',
    Normal: 'normal'
};

const PRIORITY_WIRE_TO_LABEL: Record<string, string> = {
    extreme: 'Extreme',
    high: 'High',
    normal: 'Normal'
};

const DEFAULT_SORT: SortBy = 'aging';
const VALID_SORTS: readonly SortBy[] = [
    'aging',
    'phase-aging',
    'name',
    'aligner-progress',
    'submitted',
    'approved',
    'refi-submitted',
    'refinements'
];
function parseSort(raw: string | null): SortBy {
    return raw && (VALID_SORTS as readonly string[]).includes(raw) ? (raw as SortBy) : DEFAULT_SORT;
}

// Default sort direction per sort key.
const DEFAULT_DIR_BY_SORT: Record<SortBy, SortDir> = {
    aging: 'asc',
    'phase-aging': 'asc',
    name: 'asc',
    'aligner-progress': 'desc',
    submitted: 'asc',
    approved: 'asc',
    'refi-submitted': 'asc',
    refinements: 'desc'
};
function resolveDir(raw: string | null, sort: SortBy): SortDir {
    if (raw === 'asc' || raw === 'desc') return raw;
    return DEFAULT_DIR_BY_SORT[sort];
}

interface ActionTarget {
    workCase: WorkQueueCase;
    outgoingAction: OutgoingAction;
}

function buildTotalFromGroups(groups: ReturnType<typeof useGroups>['data']): number {
    if (!groups?.groups) return 0;
    return groups.groups.reduce((sum, g) => sum + g.count, 0);
}

const TASK_SCOPED_PARAMS = [TASK_FLOW_PARAM, DM_CONTROL_PARAM, DM_CONTROL_STEP_PARAM];

export function WorkQueue() {
    usePageTitle('Work queue');
    const [searchParams, setSearchParams] = useSearchParams();
    const stepSlug = searchParams.get('step');
    const activeTaskCaseId = searchParams.get('task');
    // task and case are mutually exclusive - task wins when both are present.
    const selectedCaseId = activeTaskCaseId ? null : searchParams.get('case');
    const sortBy = parseSort(searchParams.get('sort'));
    const dir = resolveDir(searchParams.get('dir'), sortBy);

    const updateParams = useCallback(
        (updates: Record<string, string | null>) => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                for (const [k, v] of Object.entries(updates)) {
                    if (v === null || v === '') next.delete(k);
                    else next.set(k, v);
                }
                // A task's flow and DM modal belong to that task; they end when the task changes.
                if ('task' in updates) TASK_SCOPED_PARAMS.forEach((param) => next.delete(param));
                return next;
            });
        },
        [setSearchParams]
    );

    // Read-only appointments calendar overlay, same push-on-open /
    // replace-on-close pattern as the dashboard's `?appointment=` param.
    const appointmentsOpen = searchParams.get('appointments') === 'open';
    const handleOpenAppointments = useCallback(() => {
        updateParams({ appointments: 'open' });
    }, [updateParams]);
    const handleCloseAppointments = useCallback(() => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                APPOINTMENTS_OVERLAY_PARAMS.forEach((param) => next.delete(param));
                return next;
            },
            { replace: true }
        );
    }, [setSearchParams]);

    const [actionTarget, setActionTarget] = useState<ActionTarget | null>(null);
    // Case id whose row should be scrolled into view once it renders. Armed by
    // a search-dropdown selection, by the deep-link jump, and on mount when the
    // URL already carries ?case= (refresh after a search) — so the row is
    // brought on-screen in all three arrival paths, not just the dropdown one.
    const [focusCaseId, setFocusCaseId] = useState<string | null>(() =>
        searchParams.get('task') ? null : searchParams.get('case')
    );
    // Set to true by navigations that arm focusCaseId themselves (search select,
    // deep-link jump, mount-time filter restore) so the filter-change effect
    // does not immediately clear the focus id that was just set.
    const preserveFocusRef = useRef(false);
    const filterSaveInitRef = useRef(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    // View mode: 'paged' (pagination footer, viewport-fit page size) or
    // 'scroll' (single fetch up to SCROLL_MAX_LIMIT, internal scrolling).
    // Default = 'scroll' to match the dashboard's PatientTable behavior.
    const [viewMode, setViewModeState] = useState<ViewMode>(() => {
        if (typeof globalThis === 'undefined' || !globalThis.localStorage) return 'scroll';
        return globalThis.localStorage.getItem(VIEW_MODE_KEY) === 'paged' ? 'paged' : 'scroll';
    });
    const setViewMode = useCallback((next: ViewMode) => {
        setViewModeState(next);
        try {
            globalThis.localStorage?.setItem(VIEW_MODE_KEY, next);
        } catch {
            /* ignore */
        }
        // Switching to paged should land on page 1; scroll mode ignores currentPage
        // so resetting it is harmless but makes the eventual switch-back predictable.
        if (next === 'paged') setCurrentPage(1);
    }, []);
    // Toolbar filters. Each useUrlArrayParam derives from the URL directly so
    // the URL is the single source of truth — no local mirror, no oscillation.
    const [practiceFilter, setPracticeFilter] = useUrlArrayParam(
        'practice',
        searchParams,
        updateParams
    );
    const [assigneeFilter, setAssigneeFilter] = useUrlArrayParam(
        ASSIGNEE_URL_PARAM,
        searchParams,
        updateParams
    );
    const [teamFilter, setTeamFilter] = useUrlArrayParam('team', searchParams, updateParams);
    const [tagsFilter, setTagsFilter] = useUrlArrayParam('tags', searchParams, updateParams);
    const [monitoringFilter, setMonitoringFilter] = useUrlArrayParam(
        'monitoring',
        searchParams,
        updateParams
    );
    // Manufacturer-status values are raw portal-synced strings — the URL wire
    // value and the dropdown label are the same, so no label↔wire mapping.
    const [manufacturerStatusFilter, setManufacturerStatusFilter] = useUrlArrayParam(
        'mstatus',
        searchParams,
        updateParams
    );
    const [priorityFilter, setPriorityFilter] = useUrlArrayParam(
        'priority',
        searchParams,
        updateParams
    );

    const splitByPriority = searchParams.get('split') === 'priority';
    const handleSplitByPriority = useCallback(
        (on: boolean) => {
            updateParams({ split: on ? 'priority' : null });
        },
        [updateParams]
    );

    // Replace, not push: a migrated legacy URL left in history would migrate again on Back.
    const replaceAssigneeFilter = useCallback(
        (next: string[]) => {
            setSearchParams(
                (prev) => {
                    const params = new URLSearchParams(prev);
                    const encoded = encodeUrlArray(next);
                    if (encoded) params.set(ASSIGNEE_URL_PARAM, encoded);
                    else params.delete(ASSIGNEE_URL_PARAM);
                    return params;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );
    const { options: assigneeOptions, apiValues: resolvedAssignees } = useAssigneeFilter(
        assigneeFilter,
        ASSIGNEE_NULL_SENTINEL,
        replaceAssigneeFilter
    );

    // Wire values stored in URL → display labels shown in the toolbar dropdown.
    const monitoringDisplayFilter = useMemo(
        () => monitoringFilter.map((w) => MONITORING_WIRE_TO_LABEL[w] ?? w),
        [monitoringFilter]
    );

    // Convert display labels chosen in the toolbar back to wire values for the URL.
    const handleMonitoringFilter = useCallback(
        (labels: string[]) => {
            setMonitoringFilter(labels.map((l) => MONITORING_LABEL_TO_WIRE[l] ?? l));
        },
        [setMonitoringFilter]
    );

    // Priority: URL stores lowercase wire values; dropdown shows capitalised labels.
    const priorityDisplayFilter = useMemo(
        () => priorityFilter.map((w) => PRIORITY_WIRE_TO_LABEL[w] ?? w),
        [priorityFilter]
    );

    const handlePriorityFilter = useCallback(
        (labels: string[]) => {
            setPriorityFilter(labels.map((l) => PRIORITY_LABEL_TO_WIRE[l] ?? l));
        },
        [setPriorityFilter]
    );

    const { data: meta } = useMeta();
    const {
        data: groupsData,
        refetch: refetchGroups,
        isFetching: groupsFetching
    } = useGroups({
        practice: practiceFilter,
        assignee: resolvedAssignees,
        team: teamFilter,
        tags: tagsFilter,
        monitoring: monitoringFilter,
        manufacturerStatus: manufacturerStatusFilter,
        priority: priorityFilter
    });

    const allStatusIds = useMemo(() => meta?.statuses.map((s) => s.id) ?? [], [meta]);
    const selectedKey = useMemo(
        () => (stepSlug ? slugToStatusId(stepSlug, allStatusIds) : null),
        [stepSlug, allStatusIds]
    );

    // Two data sources, only one active at a time via `enabled`. Paged mode
    // uses a single useQuery (one fetch per page click); scroll mode uses
    // useInfiniteQuery with a small chunk size and an IntersectionObserver in
    // CaseList that drives `fetchNextPage` as the user scrolls.
    const pagedQuery = useCases({
        key: selectedKey,
        practice: practiceFilter,
        assignee: resolvedAssignees,
        team: teamFilter,
        tags: tagsFilter,
        monitoring: monitoringFilter,
        manufacturerStatus: manufacturerStatusFilter,
        priority: priorityFilter,
        sortBy,
        dir,
        limit: pageSize,
        page: currentPage,
        splitByPriority,
        enabled: viewMode === 'paged'
    });
    const infiniteQuery = useInfiniteCases({
        key: selectedKey,
        practice: practiceFilter,
        assignee: resolvedAssignees,
        team: teamFilter,
        tags: tagsFilter,
        monitoring: monitoringFilter,
        manufacturerStatus: manufacturerStatusFilter,
        priority: priorityFilter,
        sortBy,
        dir,
        limit: SCROLL_PAGE_SIZE,
        splitByPriority,
        enabled: viewMode === 'scroll'
    });

    // Unified view for CaseList. In scroll mode we flatten all loaded pages
    // into a single array; `total` is read from the most recent page since the
    // backend returns it on every response.
    const scrollPages = infiniteQuery.data?.pages ?? [];
    const lastScrollPage = scrollPages[scrollPages.length - 1];
    const casesData =
        viewMode === 'scroll'
            ? lastScrollPage && {
                  cases: scrollPages.flatMap((p) => p.cases),
                  total: lastScrollPage.total,
                  page: lastScrollPage.page,
                  limit: lastScrollPage.limit,
                  totalPages: lastScrollPage.totalPages
              }
            : pagedQuery.data;
    const casesLoading = viewMode === 'scroll' ? infiniteQuery.isLoading : pagedQuery.isLoading;
    const casesFetching = viewMode === 'scroll' ? infiniteQuery.isFetching : pagedQuery.isFetching;
    const refetchCases = viewMode === 'scroll' ? infiniteQuery.refetch : pagedQuery.refetch;
    const hasMore = viewMode === 'scroll' ? infiniteQuery.hasNextPage : false;
    const isLoadingMore = viewMode === 'scroll' ? infiniteQuery.isFetchingNextPage : false;
    const handleLoadMore = useCallback(() => {
        if (
            viewMode === 'scroll' &&
            infiniteQuery.hasNextPage &&
            !infiniteQuery.isFetchingNextPage
        ) {
            void infiniteQuery.fetchNextPage();
        }
    }, [viewMode, infiniteQuery]);

    // priorityCounts: take from the first page of whichever query is active.
    // The server sends the same counts on every page (they're computed over the
    // full step + filters), so first-page is correct for both modes.
    const priorityCounts: PriorityCounts | undefined =
        viewMode === 'scroll'
            ? infiniteQuery.data?.pages[0]?.priorityCounts
            : pagedQuery.data?.priorityCounts;

    const statusLookup = useMemo(() => buildStatusLookup(meta), [meta]);
    const totalCases = buildTotalFromGroups(groupsData);

    const selectedGroup = useMemo(
        () => groupsData?.groups.find((g) => g.key === selectedKey),
        [groupsData, selectedKey]
    );

    // Case found within the currently-loaded list (normal click / back-forward nav).
    const selectedInList = useMemo(
        () =>
            selectedCaseId ? (casesData?.cases.find((c) => c.id === selectedCaseId) ?? null) : null,
        [selectedCaseId, casesData]
    );

    // Deep-link fallback: when ?case=<id> points at a case that isn't in the loaded
    // list (manually-typed URL → different group/filter/page), fetch it directly so
    // the detail panel can open even before the list jumps to its group.
    const needCaseFetch = !!selectedCaseId && !selectedInList;
    const { data: fetchedCase } = useCase(selectedCaseId, needCaseFetch);

    const selectedCase = selectedInList ?? (needCaseFetch ? (fetchedCase ?? null) : null);

    // Pinned case: shown at the top of CaseList whenever the selected case is
    // not present in the currently-loaded rows — a search selection, a refresh
    // with ?case= pointing beyond the loaded page, or a filter that hides the
    // open case. Deliberately keyed on the URL selection rather than the
    // focusCaseId scroll state: focus is transient (and can be cleared by
    // unrelated filter-effect firings), while "the open case must stay visible"
    // holds for as long as the case is selected. Once the case appears in the
    // list, selectedInList is non-null and the pin dissolves into the regular
    // row — scrolled to via the one-shot useFocusCaseScroll when focus is armed.
    const pinnedCase = selectedCaseId && !selectedInList ? selectedCase : null;

    const activeTaskInList = useMemo(
        () =>
            activeTaskCaseId
                ? (casesData?.cases.find((c) => c.id === activeTaskCaseId) ?? null)
                : null,
        [activeTaskCaseId, casesData]
    );
    // Fallback fetch: when the user clicks a task action after arriving via
    // search (the case was never loaded into the list), the lookup above returns
    // null and TaskSurface silently drops. Mirrors the selectedCase pattern.
    const needTaskCaseFetch = !!activeTaskCaseId && !activeTaskInList;
    const { data: fetchedTaskCase } = useCase(activeTaskCaseId, needTaskCaseFetch);
    const activeTaskCase =
        activeTaskInList ?? (needTaskCaseFetch ? (fetchedTaskCase ?? null) : null);

    // Freeze the case + status meta for the lifetime of the open task, mirroring
    // TaskSurface's own internal freeze. After an advance the case leaves its
    // status group's list and the lookup above briefly resolves null while the
    // fallback fetch runs - without this freeze that unmounts TaskSurface and
    // destroys the completion card mid-display.
    const frozenTaskRef = useRef<{ workCase: WorkQueueCase; statusMeta: StatusMeta } | null>(null);
    if (!activeTaskCaseId) {
        frozenTaskRef.current = null;
    } else if (frozenTaskRef.current?.workCase.id !== activeTaskCaseId) {
        const meta = activeTaskCase
            ? metaFor(statusLookup.get(activeTaskCase.statusId), activeTaskCase.version)
            : undefined;
        frozenTaskRef.current =
            activeTaskCase && meta ? { workCase: activeTaskCase, statusMeta: meta } : null;
    }
    const activeTask = frozenTaskRef.current;
    const { focused: taskFocused, toggleFocus: handleToggleTaskFocus } = useTaskFocusMode(
        activeTask !== null
    );

    const handleSelect = useCallback(
        (key: string) => {
            updateParams({ step: statusIdToSlug(key), case: null, task: null });
            setCurrentPage(1);
            setFocusCaseId(null);
        },
        [updateParams]
    );

    // Capture the case id that was in the URL at mount time. Only that id is
    // eligible for the deep-link jump below — a case id set mid-session by the
    // user clicking a row is NOT a deep link and must not clobber their filters.
    const initialCaseIdRef = useRef(selectedCaseId);

    // Deep-link jump: once the fallback fetch resolves a case that isn't visible
    // in the current group, switch the left-nav to its group and clear filters
    // that could hide it, so the row appears + highlights (the panel is already
    // open via the fallback). Runs once per deep-linked case id.
    // Guard: only fires when the case id matches the mount-time value so that
    // mid-session filter changes that hide the open case do not wipe the user's
    // filters.
    const deepLinkJumpRef = useRef<string | null>(null);
    useEffect(() => {
        if (!selectedCaseId || selectedInList || !fetchedCase) return;
        if (selectedCaseId !== initialCaseIdRef.current) return;
        if (deepLinkJumpRef.current === selectedCaseId) return;
        // A refresh restores the full URL (step + filters + case) and is
        // indistinguishable from a pasted deep link by the mount-time heuristic
        // above. When the fetched case still belongs to the step in the URL, the
        // user's filters were not hiding it before the reload — jumping would only
        // wipe their filters (and, via the filter-save effect, the saved copy).
        // Wait for meta so the step slug can resolve before deciding; otherwise
        // the case fetch racing the meta load would sneak past this guard.
        if (stepSlug && allStatusIds.length === 0) return;
        if (fetchedCase.statusId === selectedKey) return;
        deepLinkJumpRef.current = selectedCaseId;
        // Arm the scroll-into-view for when the case's row lands in its group's
        // list, and protect it from the filter-change effect this jump triggers.
        preserveFocusRef.current = true;
        setFocusCaseId(selectedCaseId);
        updateParams({
            step: statusIdToSlug(fetchedCase.statusId),
            practice: null,
            assignee: null,
            team: null,
            tags: null,
            monitoring: null,
            mstatus: null,
            priority: null
        });
        setCurrentPage(1);
    }, [
        selectedCaseId,
        selectedInList,
        fetchedCase,
        updateParams,
        stepSlug,
        allStatusIds,
        selectedKey
    ]);

    // Search selection: focus the chosen case just like a deep link, clearing
    // any filters that could hide it, and navigate to its group step. Also arms
    // the automatic scroll-into-view by setting focusCaseId.
    const handleSearchSelect = useCallback(
        (result: WorkQueueCase) => {
            pushSearchHistory({
                id: result.id,
                name: result.name,
                practice: result.practice,
                profileThumbnailUrl: result.profileThumbnailUrl
            });
            preserveFocusRef.current = true;
            updateParams({
                step: statusIdToSlug(result.statusId),
                case: result.id,
                task: null,
                practice: null,
                assignee: null,
                team: null,
                tags: null,
                monitoring: null,
                mstatus: null,
                priority: null
            });
            setCurrentPage(1);
            setFocusCaseId(result.id);
        },
        [updateParams]
    );

    const handleFocusHandled = useCallback(() => {
        setFocusCaseId(null);
    }, []);

    const handleSelectCase = useCallback(
        (id: string | null) => {
            updateParams({ case: id, task: null });
            // Selecting a different case (or closing the panel) ends the pinned state.
            setFocusCaseId(null);
        },
        [updateParams]
    );

    const handleAction = useCallback((c: WorkQueueCase, outgoingAction: OutgoingAction) => {
        setActionTarget({ workCase: c, outgoingAction });
    }, []);

    const handleStartTask = useCallback(
        (id: string) => {
            updateParams({ task: id, case: null });
            setFocusCaseId(null);
        },
        [updateParams]
    );

    // "Next step" from the completion card: cycle the task URL param so that
    // TaskSurface unmounts and remounts with the freshly-fetched case data
    // (the work queue was already invalidated when the action completed).
    const pendingContinueCaseIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (activeTaskCaseId === null && pendingContinueCaseIdRef.current !== null) {
            const id = pendingContinueCaseIdRef.current;
            pendingContinueCaseIdRef.current = null;
            updateParams({ task: id });
        }
    }, [activeTaskCaseId, updateParams]);

    const handleContinueCase = useCallback(
        (caseId: string) => {
            pendingContinueCaseIdRef.current = caseId;
            updateParams({ task: null });
        },
        [updateParams]
    );

    const nextCase = useNextCaseLink({
        activeTaskCaseId,
        continuePending: pendingContinueCaseIdRef.current !== null,
        casesData,
        paged: viewMode === 'paged',
        filters: {
            team: teamFilter,
            assignee: optionLabels(assigneeFilter, assigneeOptions),
            priority: priorityDisplayFilter,
            practice: practiceFilter,
            tags: tagsFilter,
            monitoring: monitoringDisplayFilter,
            manufacturerStatus: manufacturerStatusFilter
        },
        sort: { sortBy, dir },
        defaultSort: { sortBy: DEFAULT_SORT, dir: DEFAULT_DIR_BY_SORT[DEFAULT_SORT] },
        statusLookup,
        startTask: handleStartTask,
        selectCase: handleSelectCase,
        focusCase: setFocusCaseId
    });

    const handleCloseTask = useCallback(() => {
        updateParams({ task: null });
        setFocusCaseId(null);
    }, [updateParams]);

    const handlePageChange = useCallback((next: number) => {
        setCurrentPage(next);
    }, []);

    const handleSortChange = useCallback(
        (clicked: SortBy) => {
            if (clicked === sortBy) {
                const nextDir = dir === 'asc' ? 'desc' : 'asc';
                // Store dir only when it differs from this sort's default, keeping URLs clean.
                updateParams({ dir: nextDir === DEFAULT_DIR_BY_SORT[clicked] ? null : nextDir });
            } else {
                updateParams({
                    sort: clicked === DEFAULT_SORT ? null : clicked,
                    dir: null
                });
            }
        },
        [sortBy, dir, updateParams]
    );

    const handleRefresh = useCallback(() => {
        void refetchGroups();
        void refetchCases();
    }, [refetchGroups, refetchCases]);

    // Skip this effect's mount run: focusCaseId may start armed from the URL
    // (refresh with ?case=) and every effect fires once on mount regardless of
    // whether its deps "changed" — without the guard that initial focus would be
    // cleared before the list ever renders.
    const filterResetInitRef = useRef(false);
    useEffect(() => {
        if (!filterResetInitRef.current) {
            filterResetInitRef.current = true;
            return;
        }
        setCurrentPage(1);
        if (preserveFocusRef.current) {
            // This change was triggered by a navigation that armed the focus id
            // itself (search select / deep-link jump / filter restore) — keep it.
            preserveFocusRef.current = false;
            return;
        }
        setFocusCaseId(null);
    }, [
        selectedKey,
        practiceFilter,
        resolvedAssignees,
        teamFilter,
        tagsFilter,
        monitoringFilter,
        manufacturerStatusFilter,
        priorityFilter,
        sortBy,
        dir,
        pageSize
    ]);

    const { setTitle, setSubtitle } = usePageHeader();
    useEffect(() => {
        setTitle('Work Queue');
        setSubtitle(`${totalCases.toLocaleString()} cases · ${formatToday()}`);
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [totalCases, setTitle, setSubtitle]);

    useEffect(() => {
        if (!filterSaveInitRef.current) {
            filterSaveInitRef.current = true;
            return;
        }
        const toSave: Record<string, string> = {};
        // Saved filters are restored straight into the URL, so they must carry
        // the same comma escaping the URL params use.
        if (practiceFilter.length) toSave.practice = encodeUrlArray(practiceFilter);
        if (assigneeFilter.length) toSave.assignee = encodeUrlArray(assigneeFilter);
        if (teamFilter.length) toSave.team = encodeUrlArray(teamFilter);
        if (tagsFilter.length) toSave.tags = encodeUrlArray(tagsFilter);
        if (monitoringFilter.length) toSave.monitoring = encodeUrlArray(monitoringFilter);
        if (manufacturerStatusFilter.length)
            toSave.mstatus = encodeUrlArray(manufacturerStatusFilter);
        if (priorityFilter.length) toSave.priority = encodeUrlArray(priorityFilter);
        if (sortBy !== DEFAULT_SORT) toSave.sort = sortBy;
        if (dir !== DEFAULT_DIR_BY_SORT[sortBy]) toSave.dir = dir;
        if (splitByPriority) toSave.split = 'priority';
        try {
            globalThis.localStorage?.setItem(FILTERS_STORAGE_KEY, JSON.stringify(toSave));
        } catch {
            /* quota / private-mode guard */
        }
    }, [
        practiceFilter,
        assigneeFilter,
        teamFilter,
        tagsFilter,
        monitoringFilter,
        manufacturerStatusFilter,
        priorityFilter,
        sortBy,
        dir,
        splitByPriority
    ]);

    // On mount: if the URL has no filter params, restore the last-used filters
    useEffect(() => {
        const FILTER_KEYS = [
            'practice',
            'assignee',
            'team',
            'tags',
            'monitoring',
            'mstatus',
            'priority',
            'sort',
            'dir',
            'split'
        ] as const;
        const hasFiltersInUrl = FILTER_KEYS.some((k) => searchParams.has(k));
        if (hasFiltersInUrl) return;
        try {
            const raw = globalThis.localStorage?.getItem(FILTERS_STORAGE_KEY);
            if (!raw) return;
            const saved = JSON.parse(raw) as Record<string, string>;
            if (Object.keys(saved).length === 0) return;
            // Restoring filters must not clear a mount-time focus (refresh with
            // ?case= in the URL) — the restored filters may hide the case, which is
            // exactly when the pinned row + armed focus matter most. Only arm the
            // flag when the restore touches a key the filter-reset effect watches:
            // a split-only restore never fires that effect, and an armed flag would
            // leak into (and swallow) the user's next real filter change.
            if (Object.keys(saved).some((k) => k !== 'split')) {
                preserveFocusRef.current = true;
            }
            updateParams(saved);
        } catch {
            /* corrupt JSON guard */
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Mount only — we want the URL state at the moment the page loads

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            {/* Toolbar - search + filters, sits between stats and body (matches Conversations) */}
            {!taskFocused && (
                <WorkQueueToolbar
                    statusLookup={statusLookup}
                    onSearchSelect={handleSearchSelect}
                    practiceFilter={practiceFilter}
                    onPracticeFilter={setPracticeFilter}
                    assigneeOptions={assigneeOptions}
                    assigneeFilter={assigneeFilter}
                    onAssigneeFilter={setAssigneeFilter}
                    teamFilter={teamFilter}
                    onTeamFilter={setTeamFilter}
                    tagsFilter={tagsFilter}
                    onTagsFilter={setTagsFilter}
                    monitoringFilter={monitoringDisplayFilter}
                    onMonitoringFilter={handleMonitoringFilter}
                    manufacturerStatusFilter={manufacturerStatusFilter}
                    onManufacturerStatusFilter={setManufacturerStatusFilter}
                    priorityFilter={priorityDisplayFilter}
                    onPriorityFilter={handlePriorityFilter}
                    splitByPriority={splitByPriority}
                    onSplitByPriority={handleSplitByPriority}
                    onOpenAppointments={handleOpenAppointments}
                />
            )}

            <div
                className={`flex-1 flex gap-3 overflow-hidden min-h-0 ${taskFocused ? 'p-2' : 'px-4 pb-4'}`}
            >
                {!taskFocused && (
                    <GroupNavColumn
                        selectedKey={selectedKey}
                        onSelect={handleSelect}
                        groups={groupsData?.groups}
                        meta={meta}
                        filterActive={practiceFilter.length > 0 || assigneeFilter.length > 0}
                        teamFilter={teamFilter}
                    />
                )}

                {/* Middle column - TaskSurface (when a task is active) or CaseList.
                    CaseList's DataTable brings its own card chrome, so the wrapper
                    only paints the card for the task surface. */}
                <div
                    className={`flex-1 overflow-hidden flex flex-col min-w-0 ${
                        activeTask ? 'rounded-xl border border-gray-200 bg-white' : ''
                    }`}
                >
                    {activeTask ? (
                        <TaskSurface
                            key={activeTask.workCase.id}
                            workCase={activeTask.workCase}
                            statusMeta={activeTask.statusMeta}
                            onClose={handleCloseTask}
                            onContinue={() => handleContinueCase(activeTask.workCase.id)}
                            nextCase={nextCase}
                            focusMode={taskFocused}
                            onToggleFocusMode={handleToggleTaskFocus}
                        />
                    ) : (
                        <CaseList
                            cases={casesData?.cases}
                            total={casesData?.total ?? selectedGroup?.count ?? 0}
                            totalPages={casesData?.totalPages ?? 1}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                            onPageSizeChange={setPageSize}
                            viewMode={viewMode}
                            onViewModeChange={setViewMode}
                            hasMore={hasMore}
                            isLoadingMore={isLoadingMore}
                            onLoadMore={handleLoadMore}
                            selectedKey={selectedKey}
                            selectedCaseId={selectedCaseId}
                            onSelectCase={handleSelectCase}
                            onAction={handleAction}
                            onStartTask={handleStartTask}
                            statusLookup={statusLookup}
                            isLoading={casesLoading}
                            sortBy={sortBy}
                            dir={dir}
                            onSortChange={handleSortChange}
                            onRefresh={handleRefresh}
                            isRefreshing={casesFetching || groupsFetching}
                            focusCaseId={focusCaseId}
                            onFocusHandled={handleFocusHandled}
                            pinnedCase={pinnedCase}
                            splitByPriority={splitByPriority}
                            priorityCounts={priorityCounts}
                        />
                    )}
                </div>

                <div
                    className={`flex-shrink-0 rounded-xl overflow-hidden transition-[width,border-color,margin] duration-200 ease-out ${
                        selectedCase && !activeTask
                            ? 'w-[340px] border border-gray-200 bg-white'
                            : 'w-0 -ml-3'
                    }`}
                >
                    {selectedCase && !activeTask && (
                        <DetailPanel
                            workCase={selectedCase}
                            statusMeta={metaFor(
                                statusLookup.get(selectedCase.statusId),
                                selectedCase.version
                            )}
                            meta={meta}
                            onClose={() => {
                                updateParams({ case: null });
                                setFocusCaseId(null);
                            }}
                            onAction={handleAction}
                            onStartTask={handleStartTask}
                        />
                    )}
                </div>
            </div>

            {/* Action modal */}
            <ActionModal actionTarget={actionTarget} onClose={() => setActionTarget(null)} />

            {appointmentsOpen && <AppointmentsOverlay onClose={handleCloseAppointments} />}
        </div>
    );
}
